export interface Volume {
  icon: string
  number: string
  title: string
  subtitle: string
  problem: string
  bullets: string[]
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: '📗',
    number: 'Volume 1 — 25 Lessons',
    title: 'Grip, Setup, Posture, Alignment & Short Game Foundations',
    subtitle: 'The foundation your students need to feel confident, supported and prepared from day one',
    problem:
      'Teaching absolute beginners is one of the most rewarding — and most demanding — parts of being a golf instructor. This volume gives you everything you need to guide new students with clarity, patience and structure — without improvising a single cue.',
    bullets: [
      'How to introduce grip fundamentals — hand placement, thumb position, pressure points, and one-hand and two-hand variations from the very first session',
      'How to teach setup, posture and address position — ball position, stance width, spine angle, knee flex and weight distribution',
      'How to sequence alignment training — target lines, body alignment, clubface direction and building a reliable pre-shot routine',
      'How to teach putting mechanics — grip, stance, eye position, pendulum stroke, distance control and reading basic green slopes',
      'How to introduce chipping and pitching — contact, loft selection, trajectory awareness and basic distance management around the green',
    ],
    result: 'Your beginners feel supported, capable and motivated to return — from their very first lesson.',
    popular: true,
  },
  {
    icon: '📘',
    number: 'Volume 2 — 25 Lessons',
    title: 'Full Swing Fundamentals, Irons & Ball Striking',
    subtitle: 'Structured, progressive and ready to teach at every step of the way',
    problem:
      'The full swing is the most requested — and most difficult — area to teach consistently week after week. This volume gives you 25 fully structured sessions with varied technical approaches, so every lesson feels intentional, progressive and complete.',
    bullets: [
      'How to develop swing-specific fundamentals — takeaway, backswing position, club plane, top of swing and transition into the downswing',
      'How to teach impact and ball striking — weight transfer, hip rotation, lag, contact quality and compression drills',
      'How to sequence iron play — distance control, trajectory, divot pattern, target accuracy and shot shaping introductions',
      'How to build consistency progressions — tempo training, rhythm drills, feedback loops and the most common fault corrections',
      'How to prepare students for follow-through and finish — extension, rotation, balance and full body coordination',
    ],
    result:
      'You teach the full swing with confidence and consistency — and your students feel clear, measurable progress every week.',
  },
  {
    icon: '📙',
    number: 'Volume 3 — 25 Lessons',
    title: 'Fairway Woods, Hybrids, Driver Development & Distance Control',
    subtitle: 'Technique with intention — fully guided from the first drill to the last progression',
    problem:
      'Teaching longer clubs and driver development demands precise cues, smooth progressions and a clear sense of build-up. This volume removes all planning pressure and gives you sessions that flow naturally from setup to full execution.',
    bullets: [
      'How to teach hybrid and fairway wood technique — setup adjustments, ball position, sweep vs. downward strike, contact and trajectory management',
      'How to develop driver confidence — tee height, stance width, launch angle, face angle awareness and power generation without sacrificing control',
      'How to introduce distance control as a skill — carry vs. total distance, wind adjustment, club selection habits and strategic thinking',
      'How to work on bunker play — greenside technique, long bunker shots, setup variations, sand condition adjustments and common errors',
      'How to build longer shot sequences — linking full swing, distance control and club selection into cohesive practice scenarios',
    ],
    result:
      'Your students gain real confidence with longer clubs — and you deliver those lessons with a complete, structured plan every time.',
  },
  {
    icon: '📕',
    number: 'Volume 4 — 25 Lessons',
    title: 'Course Management, Advanced Technique, Assessment & Annual Planning',
    subtitle: 'The sessions your intermediate and advanced students need — and the hardest ones to organize well',
    problem:
      'Teaching intermediate and advanced students requires a completely different kind of preparation — longer skill sequences, strategic thinking, performance management and full yearly planning. This volume gives you everything structured so you can focus entirely on your students.',
    bullets: [
      'How to teach course management — pre-shot routines, shot selection, risk vs. reward decisions, target management and playing to strengths',
      'How to develop advanced technique — shot shaping, trajectory control, spin management, playing from difficult lies and scoring shots',
      'How to guide the scoring game — pressure putting, up-and-down conversions, scrambling strategy and mental resilience',
      'How to run initial, monthly and final assessments — and organize complete 4, 8 and 12-week training cycles across a full year',
      'How to distribute the 100 lessons into technical weeks, review weeks, fitness weeks, assessment weeks and performance preparation weeks',
    ],
    result:
      'Your advanced students grow as complete players — and you lead that process with a fully professional structure.',
  },
]
